<script setup>
import { useI18n } from 'vue-i18n';
import messages from 'locales/BoardNotesAccordion';
import { computed } from 'vue';

const { t } = useI18n({ messages });

const emit = defineEmits(['noteClick']);

const { notes = [] } = defineProps({ notes: Array });
const NOTE_PRIORITY = {
  critical: 1,
  release_required: 2,
  warning: 3,
  relevant: 4,
  internal: 5,
};
const groupedNotes = computed(() => {
  const categoryMap = Map.groupBy(notes, (note) => note.category);
  return Array.from(categoryMap.values())
    .map((categoryNotes) =>
    ({
        description: categoryNotes[0].description,
        title: categoryNotes[0].title,
        category: categoryNotes[0].category,
        priority: categoryNotes[0].priority,
        notes: categoryNotes,
      }))
    .sort((group1, group2) => NOTE_PRIORITY[group1.priority] - NOTE_PRIORITY[group2.priority]);
});

function caseDescription(note, index) {
  if (note.side && note.side !== 'none') {
    return t(`BoardNotesAccordion.case_no_${note.side}`, { index: index + 1 });
  }

  if (note.shapes.length === 0) {
    return t(`BoardNotesAccordion.case_${note.side}`);
  }
  return t('BoardNotesAccordion.case_no', { index: index + 1 });
}

function caseDetailsAvailable(categoryNotes) {
  if (categoryNotes.length !== 1) return true;

  const note = categoryNotes[0];
  return note.side !== 'none' || note.identifiers.length !== 0 || note.shapes.length !== 0;
}
</script>

<template>
  <div
    class="accordion accordion-flush"
    id="notesAccordion"
    style="--bs-accordion-btn-focus-box-shadow: none"
  >
    <div class="accordion-item" v-for="group in groupedNotes" :key="group.category">
      <h2 class="accordion-header">
        <button
          :class="['accordion-button', { collapsed: !(group.priority === 'critical') }]"
          type="button"
          data-bs-toggle="collapse"
          :data-bs-target="`#note${group.category}`"
          :aria-expanded="group.priority === 'critical'"
          :aria-controls="`note${group.category}`"
        >
          <i
            v-if="group.priority === 'critical'"
            class="fas fa-minus-circle text-danger text-center"
          />
          <i
            v-if="group.priority === 'release_required'"
            class="fas fa-headset text-secondary-emphasis text-center"
          />
          <i
            v-if="group.priority === 'warning'"
            class="fas fa-exclamation-triangle text-warning text-center"
          />
          <i
            v-if="group.priority === 'relevant'"
            class="fas fa-info-circle text-info text-center"
          />
          <i
            v-if="group.priority === 'internal'"
            class="fas fa-glasses text-secondary text-center"
          />
          <span class="ps-3">
            {{ group.title }}
          </span>
        </button>
      </h2>
      <div
        :id="`note${group.category}`"
        :class="['accordion-collapse', 'collapse', { show: group.priority === 'critical' }]"
        data-bs-parent="#notesAccordion"
      >
        <div>
          <div
            class="accordion-body"
            v-html="group.description"
          />

          <hr class="my-0">

          <template v-if="caseDetailsAvailable(group.notes)">
            <div v-for="(note, index) in group.notes" :key="note.id">
              <div
                @click="emit('noteClick', note)"
                :class="{ 'active': note.selected }"
                class="note-link w-100 d-flex justify-content-between py-2 px-3"
                href="#"
              >
                <div v-if="note.identifiers.length === 0" class="case-number">
                  {{ caseDescription(note, index) }}
                </div>
                <div v-else class="case-number">
                  {{ note.identifiers.join(', ') }}
                </div>

                <div v-if="note.shapes.length !== 0">
                  <i v-if="note.selected" class="indicator fas fa-eye" />
                  <i v-else class="indicator fas fa-eye-slash" />
                </div>
              </div>

              <hr class="my-0">
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.note-link {
  $note-bg-color: var(--bs-accordion-active-bg);

  cursor: pointer;

  &:hover {
    background-color: $note-bg-color;
  }

  &.active {
    background-color: $note-bg-color;
    color: var(--bs-accordion-active-color);
  }
}
</style>
